import React from "react";
import { motion } from "framer-motion";
import ReactTooltip from "react-tooltip";
import { ApiSubscriptionPlan } from "src/api/types";
import useWindowSize from "src/hooks/useWindowSize";
import Badge from "src/components/Badge";
import Button from "src/components/Button";
import useAuth from "src/hooks/selectors/useAuth";
import { useTranslation } from "react-i18next";
import SubscriptionPlanPrice from "src/components/subscription/SubscriptionPlanPrice";
import Indicator from "src/components/layout/Indicator";
import useSubscription from "src/hooks/selectors/useSubscription";
import usePlans from "src/hooks/api/usePlans";

interface SubscriptionPlanProps {
	plan: ApiSubscriptionPlan;
	isRecommended?: boolean;
	isSelected?: boolean;
	onSelect?: (plan?: ApiSubscriptionPlan) => void;
}

const SubscriptionPlan = ({
	plan,
	isRecommended,
	isSelected,
	onSelect,
}: SubscriptionPlanProps) => {
	const { interval } = usePlans();
	const subscription = useSubscription();
	const { t } = useTranslation("subscription", {
		keyPrefix: "subscription-plan",
	});
	const size = useWindowSize();
	const auth = useAuth();
	const promos = auth?.company?.promos || [];
	const can_use_trial = auth?.company?.statuses?.can_use_trial;
	if (size.isPhone && isSelected) {
		return <></>;
	}
	const canUpgrade = plan.statuses.can_upgrade;

	return (
		<motion.div
			data-tip
			data-for={`tooltip-subscription-${plan.slug}`}
			animate={{
				opacity: 1,
				y: 0,
				transition: {
					delay: isRecommended ? 0.3 : 0,
				},
			}}
			initial={{
				opacity: 0,
				y: isRecommended ? -200 : -100,
			}}
			className={`rounded-md flex flex-col relative  shadow
                ${
					isRecommended
						? "z-[60] bg-dark-accent text-white -mx-2"
						: `bg-dark text-white ${
								isSelected ? "z-[60]" : ""
						  } my-${isSelected ? 0 : 8}`
				}
            `}
		>
			{!canUpgrade && (
				<ReactTooltip
					id={`tooltip-subscription-${plan.slug}`}
					effect="solid"
				>
					<div className="max-w-[200px] text-center font-bold">
						{t("cant-change")}
					</div>
				</ReactTooltip>
			)}
			{isRecommended && (
				<div className="absolute w-max uppercase top-2 md:top-6 -right-2 md:-right-4 z-[80]">
					<Badge
						variant="primary"
						className="w-max uppercase border-0 text-sm py-2"
					>
						{t("most-common")}
					</Badge>
				</div>
			)}
			<div className="px-8 py-8 gap-8 pt-12 flex flex-col flex-1">
				<SubscriptionPlanPrice
					discount={
						promos?.includes("euro_subscription_deal")
							? {
									price: 0.99,
							  }
							: undefined
					}
					{...{ plan }}
				/>
				<div className="flex flex-col flex-1 gap-4 mb-8">
					{plan.services.map((service) => {
						const isComingSoon = service
							.trim()
							.endsWith("(coming soon)");

						service = service.replace("(coming soon)", "");
						return (
							<div
								key={`${plan.slug}-${service}`}
								className="flex flex-row gap-2"
							>
								<div>
									<i className="fas leading-2 text-primary fa-check"></i>
								</div>
								<p className="flex items-center gap-4">
									{service}

									{isComingSoon && (
										<Indicator className="w-max rounded-sm bg-dark text-white py-3">
											Coming soon
										</Indicator>
									)}
								</p>
							</div>
						);
					})}
				</div>

				{plan.slug === "free" ? (
					<div className="flex flex-col gap-4">
						{!subscription?.id && (
							<Button disabled>{t("button.current")}</Button>
						)}
					</div>
				) : (
					<div className="flex gap-4 flex-col">
						{!isSelected && (
							<Button
								disabled={!plan.statuses.can_upgrade}
								onClick={() =>
									onSelect &&
									onSelect(
										isSelected
											? ({} as ApiSubscriptionPlan)
											: plan
									)
								}
								className="flex-1"
								type={
									isSelected || !canUpgrade
										? "gray"
										: isRecommended
										? "primary"
										: "white"
								}
							>
								{isSelected ? (
									<>
										<i className="fas fa-arrow-left mr-2"></i>
										{t("button.other-options")}
									</>
								) : (
									<>
										{t("button.choose")}
										<i className="fas fa-arrow-right ml-2"></i>
									</>
								)}
							</Button>
						)}
						<div className="flex flex-col">
							{can_use_trial &&
								!promos.includes("euro_subscription_deal") && (
									<strong
										className={`text-center text-${
											isRecommended ? "orange" : "primary"
										} mb-0`}
									>
										{t("trial", {
											days: 14,
										})}
									</strong>
								)}
							{interval !== "YEARLY" && (
								<span className="text-center">
									{plan.cancel_text}
								</span>
							)}
						</div>
					</div>
				)}
			</div>
		</motion.div>
	);
};

export default SubscriptionPlan;
